import { useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useOrdersExportStore } from '../stores/orders-export.store';

/**
 * App-wide banner for large orders CSV exports. Survives in-app navigation so users
 * get honest progress instead of a frozen list-page button (DEF-01 UX).
 */
export function OrdersCsvExportBanner() {
  const status = useOrdersExportStore((state) => state.status);
  const rowsExported = useOrdersExportStore((state) => state.rowsExported);
  const estimatedTotal = useOrdersExportStore((state) => state.estimatedTotal);
  const errorMessage = useOrdersExportStore((state) => state.errorMessage);
  const isRunning = useOrdersExportStore((state) => state.isRunning);
  const dismiss = useOrdersExportStore((state) => state.dismiss);
  const elapsedMs = useOrdersExportStore((state) => state.elapsedMs);

  useEffect(() => {
    if (!isRunning) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isRunning]);

  if (status === 'idle') return null;

  const progressPercent =
    estimatedTotal != null && estimatedTotal > 0
      ? Math.min(100, Math.round((rowsExported / estimatedTotal) * 100))
      : null;

  const progressLabel =
    estimatedTotal != null && estimatedTotal > 0
      ? `${rowsExported.toLocaleString()} of ${estimatedTotal.toLocaleString()} rows`
      : `${rowsExported.toLocaleString()} rows exported`;

  const elapsedLabel =
    elapsedMs >= 60_000
      ? `${Math.floor(elapsedMs / 60_000)}m ${Math.floor((elapsedMs % 60_000) / 1000)}s`
      : `${Math.max(1, Math.floor(elapsedMs / 1000))}s`;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="orders-csv-export-banner"
      className={cn(
        'fixed inset-x-0 bottom-0 z-toast border-t px-4 py-3 shadow-lg sm:px-6',
        status === 'error'
          ? 'border-destructive/30 bg-destructive/10'
          : status === 'success'
            ? 'border-success/30 bg-success/10'
            : 'border-border bg-background',
      )}
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex max-w-5xl items-start gap-3">
        {status === 'running' ? (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
        ) : null}

        <div className="min-w-0 flex-1">
          {status === 'running' ? (
            <>
              <p className="text-sm font-medium text-foreground">Exporting orders CSV…</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {progressLabel} · {elapsedLabel} elapsed. You can keep using TruSpace — the file
                saves automatically when ready. Closing this tab will cancel the export.
              </p>
              {progressPercent != null ? (
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
                  aria-hidden="true"
                >
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {status === 'success' ? (
            <p className="text-sm font-medium text-foreground">
              Export complete — {progressLabel} downloaded.
            </p>
          ) : null}

          {status === 'error' ? (
            <>
              <p className="text-sm font-medium text-destructive-text">Export failed</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{errorMessage ?? 'CSV export failed'}</p>
            </>
          ) : null}
        </div>

        {status !== 'running' ? (
          <Button type="button" variant="ghost" size="sm" onClick={dismiss} aria-label="Dismiss export notice">
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
